import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, X, Calendar, Clock, BookOpen, Trash2, RefreshCw, ChevronDown, ChevronUp, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const fmtSwedishDate = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return "";
  try {
    const d = new Date(yyyy_mm_dd + "T12:00:00Z");
    return d.toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" });
  } catch { return yyyy_mm_dd; }
};

const fmtRelative = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return "";
  try {
    const d = new Date(yyyy_mm_dd + "T12:00:00Z");
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Idag";
    if (diff === 1) return "Imorgon";
    if (diff < 0) return `${-diff} dagar sedan`;
    return `Om ${diff} dagar`;
  } catch { return ""; }
};

/**
 * StudyPlanModal — visar studieschema för alla barn i familjen.
 * - Barn: ser sitt eget schema.
 * - Vuxen/admin: ser flikar för varje barn, kan generera per barn eller alla.
 */
export function StudyPlanModal({ onClose }) {
  const { user: currentUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null); // null | "all" | user_id
  const [activeIdx, setActiveIdx] = useState(0);
  const [expandedTest, setExpandedTest] = useState(null);
  const [children, setChildren] = useState([]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/study-plan");
      setPlans(data.plans || []);
      if (data.plans?.length && activeIdx >= data.plans.length) setActiveIdx(0);
    } catch (_) {
      setPlans([]);
    } finally {
      setLoading(false);
    }
    // Vuxen-läge: hämta också barnen så vi vet vilka som saknar schema
    if (!currentUser?.is_child) {
      try {
        const { data } = await api.get("/users");
        setChildren((data || []).filter((u) => u.is_child));
      } catch (_) {
        setChildren([]);
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const generateAll = async () => {
    setGenerating("all");
    toast.info("Edit bygger schemat åt alla barn… (~30-60s/barn)", { duration: 120000, id: "study-gen" });
    try {
      await api.post("/study-plan/generate-all");
      toast.dismiss("study-gen");
      toast.success("Klart!");
      await load();
    } catch (e) {
      toast.dismiss("study-gen");
      toast.error(e?.response?.data?.detail || "Något gick fel");
    } finally {
      setGenerating(null);
    }
  };

  const generateForChild = async (childId) => {
    setGenerating(childId);
    toast.info("Edit bygger schemat… (~30-60s)", { duration: 60000, id: "study-gen" });
    try {
      const params = currentUser?.is_child ? "" : `?child_user_id=${childId}`;
      const { data } = await api.post(`/study-plan/generate${params}`);
      toast.dismiss("study-gen");
      if (data.ok) {
        toast.success("Schema klart!");
        await load();
      } else {
        toast.error(data.error || "Kunde inte skapa schema");
      }
    } catch (e) {
      toast.dismiss("study-gen");
      toast.error(e?.response?.data?.detail || "Något gick fel");
    } finally {
      setGenerating(null);
    }
  };

  const removePlan = async (planUserId) => {
    if (!window.confirm("Ta bort detta studieschema?")) return;
    await api.delete(`/study-plan/${planUserId}`);
    toast.success("Borttaget");
    await load();
  };

  const active = plans[activeIdx];

  // Gruppera sessioner per prov
  const sessionsByTest = {};
  (active?.sessions || []).forEach((s) => {
    const k = s.test_event_id || s.test_title;
    if (!sessionsByTest[k]) sessionsByTest[k] = { title: s.test_title, sessions: [] };
    sessionsByTest[k].sessions.push(s);
  });

  // Vilka barn saknar schema (vuxen-vy)
  const planUserIds = new Set(plans.map((p) => p.user_id));
  const missingChildren = (children || []).filter((c) => !planUserIds.has(c.id));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-6"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
      }}
      onClick={onClose}
      data-testid="study-plan-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md sm:max-w-lg rounded-2xl shadow-xl flex flex-col overflow-hidden"
        style={{ maxHeight: "calc(100vh - 48px)" }}
        data-testid="study-plan-modal"
      >
        <header className="px-5 pt-4 pb-3 border-b border-[color:var(--border)] bg-white flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#EEF2F7] text-[color:var(--jarvis)] flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--jarvis)]">
              Studieschema · Edit
            </p>
            <h2 className="text-base font-medium leading-snug">
              {active ? `${active.user_name}s schema` : "Skapa studieschema"}
            </h2>
            {active?.generated_at && (
              <p className="text-[11px] text-[color:var(--text-tertiary)] mt-0.5">
                {active.test_count} prov · {active.sessions?.length || 0} pass · genererat {new Date(active.generated_at).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}
              </p>
            )}
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)] flex-shrink-0" data-testid="study-plan-close">
            <X size={18} />
          </button>
        </header>

        {/* Barn-väljare (visas om vuxen och fler än ett barn med schema) */}
        {plans.length > 1 && (
          <div className="px-5 py-2 border-b border-[color:var(--border)] flex gap-2 overflow-x-auto" data-testid="study-plan-tabs">
            {plans.map((p, i) => (
              <button
                key={p.user_id}
                onClick={() => { setActiveIdx(i); setExpandedTest(null); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex items-center gap-1.5 ${
                  i === activeIdx
                    ? "bg-[color:var(--jarvis)] text-white"
                    : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
                }`}
                data-testid={`study-plan-tab-${p.user_name}`}
              >
                <UserIcon size={11} /> {p.user_name}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto px-5 py-4 bg-white">
          {loading ? (
            <p className="text-sm text-[color:var(--text-tertiary)] text-center mt-8">Laddar…</p>
          ) : plans.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-[color:var(--text-secondary)] mb-4 leading-relaxed">
                {currentUser?.is_child
                  ? "Du har inget aktivt studieschema. Edit kan läsa igenom dina kommande prov, övningsmaterial och mail för att bygga en dag-för-dag-plan."
                  : `Inga aktiva studiescheman. Edit kan bygga ett schema åt ${children.length > 0 ? "barnen" : "varje barn"} baserat på prov, övningsmaterial och mail.`}
              </p>
              {currentUser?.is_child ? (
                <button
                  onClick={() => generateForChild(currentUser.id)}
                  disabled={generating === currentUser.id}
                  className="px-5 py-2.5 rounded-full bg-[color:var(--jarvis)] text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="study-plan-generate-self"
                >
                  <Sparkles size={14} /> {generating === currentUser.id ? "Bygger…" : "Skapa mitt schema"}
                </button>
              ) : (
                <button
                  onClick={generateAll}
                  disabled={generating === "all"}
                  className="px-5 py-2.5 rounded-full bg-[color:var(--jarvis)] text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="study-plan-generate-all"
                >
                  <Sparkles size={14} /> {generating === "all" ? "Bygger…" : "Skapa schema åt alla barn"}
                </button>
              )}
            </div>
          ) : (
            <>
              {active?.summary && (
                <div className="rounded-2xl p-3.5 mb-4 bg-[#EEF2F7] border border-[#D8E2EE]" data-testid="study-plan-summary">
                  <p className="text-sm leading-relaxed text-[color:var(--text-primary)]">{active.summary}</p>
                </div>
              )}

              {active?.tests && active.tests.length > 0 && (
                <section className="mb-4">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
                    {active.user_name}s kommande prov ({active.tests.length})
                  </p>
                  <ul className="space-y-1.5">
                    {active.tests.map((t) => (
                      <li key={t.id || t.title} className="flex items-center gap-2 text-sm">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#FDF2EE] text-[#E07A5F] flex-shrink-0">
                          {t.owner || "Prov"}
                        </span>
                        <span className="flex-1 truncate font-medium">{t.title}</span>
                        <span className="text-xs text-[color:var(--text-tertiary)] flex-shrink-0">{fmtRelative(t.date)}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
                Studiepass ({(active?.sessions || []).length})
              </p>
              <div className="space-y-2.5">
                {Object.entries(sessionsByTest).map(([key, group]) => {
                  const expanded = expandedTest === key;
                  return (
                    <div key={key} className="card-soft p-3">
                      <button
                        onClick={() => setExpandedTest(expanded ? null : key)}
                        className="w-full flex items-center gap-2 text-left"
                      >
                        <BookOpen size={14} className="text-[color:var(--jarvis)] flex-shrink-0" />
                        <span className="flex-1 text-sm font-medium truncate">{group.title}</span>
                        <span className="text-[11px] text-[color:var(--text-tertiary)]">{group.sessions.length} pass</span>
                        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {expanded && (
                        <ul className="mt-3 space-y-2 pl-1">
                          {group.sessions.map((s, i) => (
                            <li key={s.date ? `sp-${s.date}-${i}` : `sp-${i}`} className="flex gap-3">
                              <div className="flex flex-col items-center flex-shrink-0 w-12 text-center">
                                <span className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">
                                  {fmtRelative(s.date)}
                                </span>
                                <span className="text-xs font-semibold mt-0.5">
                                  {fmtSwedishDate(s.date).split(" ")[1] || s.date.slice(-5)}
                                </span>
                                <span className="text-[10px] text-[color:var(--text-tertiary)] mt-0.5 flex items-center gap-0.5">
                                  <Clock size={9} /> {s.duration_min}m
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-[color:var(--text-secondary)] leading-relaxed">{s.focus}</p>
                                {s.sources && s.sources.length > 0 && (
                                  <ul className="mt-1.5 space-y-0.5">
                                    {s.sources.map((src, j) => (
                                      <li key={`sp-src-${i}-${j}-${(src || '').slice(0,20)}`} className="text-[10px] text-[color:var(--text-tertiary)] flex items-start gap-1">
                                        <span className="text-[color:var(--moss)]">•</span>
                                        <span className="line-clamp-2">{src}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Vuxen-vy: visa barn som saknar schema med "skapa"-knapp */}
              {!currentUser?.is_child && missingChildren.length > 0 && (
                <div className="mt-5 pt-4 border-t border-[color:var(--border)]" data-testid="study-plan-missing-children">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-tertiary)] mb-2">
                    Saknar schema
                  </p>
                  <ul className="space-y-2">
                    {missingChildren.map((c) => (
                      <li key={c.id} className="flex items-center gap-2">
                        <span className="text-sm flex-1">{c.name}</span>
                        <button
                          onClick={() => generateForChild(c.id)}
                          disabled={generating === c.id}
                          className="text-xs font-medium text-[color:var(--jarvis)] hover:underline flex items-center gap-1 disabled:opacity-50"
                          data-testid={`study-plan-generate-${c.name}`}
                        >
                          <Sparkles size={11} /> {generating === c.id ? "Bygger…" : "Skapa schema"}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {active && (
          <footer className="px-5 py-3 border-t border-[color:var(--border)] flex items-center justify-between bg-white">
            <button
              onClick={() => removePlan(active.user_id)}
              className="text-xs font-medium text-[color:var(--text-tertiary)] hover:text-[#E07A5F] flex items-center gap-1.5"
              data-testid="study-plan-delete"
            >
              <Trash2 size={11} /> Ta bort
            </button>
            <button
              onClick={() => generateForChild(active.user_id)}
              disabled={generating === active.user_id}
              className="text-xs font-medium text-[color:var(--jarvis)] hover:underline flex items-center gap-1.5 disabled:opacity-50"
              data-testid="study-plan-regenerate"
            >
              <RefreshCw size={11} className={generating === active.user_id ? "animate-spin" : ""} /> {generating === active.user_id ? "Bygger…" : "Generera om"}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

export default StudyPlanModal;
